import React from "react";
import { MapPin, FileText, CheckCircle2, Clock, AlertTriangle, EyeOff, Eye, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ROLES, getMinStaff, extractVenue, isSheetOverdue, isStudioJob } from "@/utils/constants";

export const EventPopover = ({ job, members, teamMembers, understaffed, assigned, minStaff, dayUnavail, getDisplayTitle, onOpenJobSheet, sheetStatus, editMode, onHideJob, onDeleteJob }) => (
  <div className="space-y-2">
    <p className="font-medium text-sm text-[#1f2937]">{getDisplayTitle(job)}</p>
    {job.time && <p className="text-xs text-[#6b7280]">{job.time}</p>}
    {job.location && (
      <p className="text-xs text-[#6b7280] flex items-center gap-1">
        <MapPin className="h-3 w-3 flex-shrink-0" />
        {extractVenue(job.location)}
      </p>
    )}
    <div className="flex items-center gap-2 flex-wrap">
      {job.has_video_booth && (
        <Badge className="bg-[#605e5e] text-white text-[10px]">Booth</Badge>
      )}
      {understaffed && (
        <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">
          {assigned}/{minStaff} staff
        </Badge>
      )}
      {minStaff > 0 && assigned >= minStaff && (
        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700">
          {assigned}/{minStaff} staff
        </Badge>
      )}
      {onOpenJobSheet && !isStudioJob(job) && (minStaff > 0 || job.event_name?.toLowerCase().includes('guestbook') || job.title?.toLowerCase().includes('guestbook')) && (() => {
        const sheetSent = sheetStatus?.send_status === 'sent';
        const overdue = !sheetSent && isSheetOverdue(job.date);
        return (
          <Button
            size="sm"
            variant="outline"
            className={`h-5 text-[10px] px-2 ${sheetSent ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' : overdue ? 'border-red-500 bg-red-600 text-white hover:bg-red-700 hover:text-white animate-pulse' : 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
            onClick={() => onOpenJobSheet(job.id)}
            data-testid={`popover-job-sheet-${job.id}`}
          >
            {sheetSent ? <CheckCircle2 className="h-3 w-3 mr-1" /> : overdue ? <AlertTriangle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
            {sheetSent ? 'Sheet Sent' : overdue ? 'Sheet Overdue' : 'Sheet Pending'}
          </Button>
        );
      })()}
    </div>
    <div className="border-t border-[#e5e7eb] pt-2">
      <p className="text-[10px] font-medium text-[#8d8b8b] mb-1">TEAM</p>
      {members.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {members.map((name) => (
            <Badge key={name} className="bg-[#8DA399] text-white text-[10px]">{name}</Badge>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[#8d8b8b]">No team assigned</p>
      )}
    </div>
    {dayUnavail.length > 0 && (
      <div className="border-t border-[#e5e7eb] pt-2">
        <p className="text-[10px] font-medium text-[#8d8b8b] mb-1">UNAVAILABLE</p>
        <div className="flex flex-wrap gap-1">
          {dayUnavail.map((person) => (
            <Badge key={person} variant="outline" className="text-[10px] border-red-200 text-red-600">{person}</Badge>
          ))}
        </div>
      </div>
    )}
    {editMode && (onHideJob || onDeleteJob) && (
      <div className="border-t border-[#e5e7eb] pt-2 flex items-center gap-2">
        {onHideJob && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] px-2 flex-1 border-gray-300 text-[#605e5e] hover:bg-gray-50"
            onClick={() => onHideJob(job.id)}
            data-testid={`popover-hide-${job.id}`}
          >
            {(job.is_hidden || job.hidden) ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
            {(job.is_hidden || job.hidden) ? 'Unhide' : 'Hide'}
          </Button>
        )}
        {onDeleteJob && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] px-2 flex-1 border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => onDeleteJob(job.id)}
            data-testid={`popover-delete-${job.id}`}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
        )}
      </div>
    )}
  </div>
);
